import { useQuery } from '@tanstack/react-query';
import { venuesService } from '@/services/venues.service';
import { Card, Button } from '@caruso/ui';
import { MapPin, Users, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Venue, PricingType } from '@caruso/types';
import { api } from '@/lib/axios';
import { useEffect, useState } from 'react';

export function PublicVenuesPage() {
  const navigate = useNavigate();
  const [tenantName, setTenantName] = useState('');

  const { data: venues, isLoading } = useQuery({
    queryKey: ['public-venues'],
    queryFn: () => venuesService.getAll(true),
  });

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const response = await api.get('/settings/tenant-info');
        if (response.data?.name) {
          setTenantName(response.data.name);
        }
      } catch (error) {
        console.error('Erro ao buscar informações do tenant:', error);
      }
    };

    fetchTenantInfo();
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-brand-ink">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-ink to-brand-ink/80 border-b border-brand-gold/20">
        <div className="container mx-auto px-4 py-8">
          {tenantName && (
            <p className="text-lg font-serif text-brand-gold/80 mb-2">
              {tenantName}
            </p>
          )}
          <h1 className="text-4xl font-bold text-brand-gold mb-2">
            Nossos Espaços
          </h1>
          <p className="text-brand-ivory/70">
            Encontre o ambiente perfeito para o seu evento
          </p>
        </div>
      </div>

      {/* Venues Grid */}
      <div className="container mx-auto px-4 py-12">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-brand-ivory/50">Carregando ambientes...</p>
          </div>
        ) : venues && venues.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue: Venue) => (
              <Card
                key={venue.id}
                className="bg-brand-ink/50 border-brand-gold/20 overflow-hidden hover:border-brand-gold/40 transition-all hover:scale-105"
              >
                <div className="relative h-48 bg-brand-ink/80">
                  {venue.images && venue.images.length > 0 ? (
                    <img
                      src={venue.images.find(img => img.isPrimary)?.url || venue.images[0]?.url}
                      alt={venue.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <MapPin className="w-12 h-12 text-brand-gold/30" />
                    </div>
                  )}
                  {venue.featured && (
                    <div className="absolute top-2 right-2 bg-brand-gold text-brand-ink px-2 py-1 rounded text-xs font-bold">
                      DESTAQUE
                    </div>
                  )}
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-brand-gold mb-2">
                      {venue.name}
                    </h3>
                    <p className="text-brand-ivory/60 text-sm line-clamp-2 min-h-[2.5rem]">
                      {venue.description || '\u00A0'}
                    </p>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-brand-ivory/70">
                    {venue.capacity && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>{venue.capacity} pessoas</span>
                      </div>
                    )}
                    {venue.area && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{venue.area}m²</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-brand-gold/10">
                    {venue.showPricePublicly !== false ? (
                      <div className="flex items-baseline gap-2">
                        <DollarSign className="w-5 h-5 text-brand-gold" />
                        <span className="text-2xl font-bold text-brand-gold">
                          {formatPrice(venue.defaultPrice)}
                        </span>
                        <span className="text-brand-ivory/60">
                          /{venue.pricingType === PricingType.DAILY ? 'dia' : 'hora'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg text-brand-ivory/60">
                          Consulte valores
                        </span>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => navigate(`/ambientes/${venue.id}`)}
                    variant="premium"
                    className="w-full"
                  >
                    Ver Detalhes e Reservar
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-brand-gold/50" />
            <p className="text-lg text-brand-ivory/60">
              Nenhum ambiente disponível no momento
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
